import React from "react";
import { fmtQty } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { FormField } from "@/components/stock/FormField";
import { selectCls } from "@/components/stock/LineItemsEditor";

export const KINDS = [
  { kind: "fg", id: "fg_id", qty: "fg_qty", ref: "fgs", label: "finish_goods" },
  { kind: "carton", id: "carton_id", qty: "carton_qty", ref: "cartons", label: "cartons" },
  { kind: "plastic", id: "plastic_id", qty: "plastic_qty", ref: "plastics", label: "plastics" },
];

export const emptyLine = () => ({ product_id: "", packing_style_id: "", fg_qty: 0, carton_qty: 0, plastic_qty: 0 });

const Opt = ({ list }) => list.map((o) => <option key={o.id} value={o.id}>{o.code} · {o.name}</option>);

const AllocInfo = ({ a, t, testid }) => {
  if (!a) return null;
  const ok = a.shortage <= 1e-9;
  return (
    <div data-testid={testid} className={`mt-1 text-[11px] leading-tight rounded px-1.5 py-1 ${ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
      <div className="flex items-center gap-1 font-semibold">
        {ok ? <CheckCircle2 size={11} /> : <AlertTriangle size={11} />}
        {ok ? "OK" : `${t("shortage")} ${fmtQty(a.shortage)}`}
      </div>
      <div className="font-mono text-slate-600">{t("from_stock")} {fmtQty(a.from_stock)} · {t("from_po")} {fmtQty(a.from_po)}</div>
      {a.to_buy > 1e-9 && <div className="font-mono font-semibold">{t("to_buy")} {fmtQty(a.to_buy)}</div>}
    </div>
  );
};

function LineRow({ ln, idx, refs, mapping, alloc, onChange, onRemove, canRemove, ready }) {
  const { t } = useLang();
  const tid = (k) => `ship-line-${idx}-${k}`;
  const item = (k) => refs[k.ref].find((x) => x.id === mapping?.[k.id]);
  return (
    <div className="rounded-lg border border-slate-200 p-3 bg-white" data-testid={`ship-line-${idx}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">#{idx + 1}</span>
        {canRemove && <Button variant="ghost" size="sm" className="text-red-600 h-7" onClick={onRemove} data-testid={tid("remove")}><Trash2 size={13} /></Button>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <FormField label={t("products")}>
          <select className={selectCls} value={ln.product_id || ""} onChange={(e) => onChange({ product_id: e.target.value })} data-testid={tid("product")}><option value="">—</option><Opt list={refs.products} /></select>
        </FormField>
        <FormField label={t("packing_styles")}>
          <select className={selectCls} value={ln.packing_style_id || ""} onChange={(e) => onChange({ packing_style_id: e.target.value })} data-testid={tid("pack")}><option value="">—</option><Opt list={refs.packs} /></select>
        </FormField>
      </div>
      {ready && !mapping && (
        <div className="mb-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid={tid("no-mapping")}>{t("mapping_missing")}</div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {KINDS.map((k) => (
          <FormField key={k.kind} label={`${t("qty")} ${t(k.label)}`}>
            <Input type="number" min="0" className="font-mono" value={ln[k.qty]} onChange={(e) => onChange({ [k.qty]: e.target.value })} data-testid={tid(`${k.kind}-qty`)} />
            <div className="mt-1 text-[11px] font-mono text-slate-500 truncate" data-testid={tid(`${k.kind}-item`)}>
              {item(k) ? `${item(k).code} · ${item(k).name}` : "—"}
            </div>
            <AllocInfo a={alloc.find((a) => a.line_idx === idx && a.kind === k.kind)} t={t} testid={tid(`${k.kind}-alloc`)} />
          </FormField>
        ))}
      </div>
    </div>
  );
}

export function ShipmentLinesEditor({ lines, setLines, refs, buyerId, brandId, alloc }) {
  const { t } = useLang();
  const findMap = (ln) => refs.maps.find((x) => x.buyer_id === buyerId && x.brand_id === brandId
    && x.product_id === ln.product_id && x.packing_style_id === ln.packing_style_id);
  return (
    <div className="space-y-3" data-testid="shipment-lines-editor">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-semibold text-slate-800">{t("product_lines")} ({lines.length})</h4>
          <p className="text-xs text-slate-500">{t("fill_from_mapping")} · {t("alloc_hint")}</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setLines([...lines, emptyLine()])} data-testid="ship-add-line-btn"><Plus size={13} className="mr-1" />{t("add_product_line")}</Button>
      </div>
      {lines.map((ln, i) => (
        <LineRow key={ln.line_id || `new-${i}`} ln={ln} idx={i} refs={refs} alloc={alloc} mapping={findMap(ln)}
                 ready={!!(buyerId && brandId && ln.product_id && ln.packing_style_id)}
                 onChange={(p) => setLines(lines.map((x, j) => (j === i ? { ...x, ...p } : x)))}
                 onRemove={() => setLines(lines.filter((_, j) => j !== i))} canRemove={lines.length > 1} />
      ))}
    </div>
  );
}
